import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { AgentStatus } from "@safiri/shared";
import { api, setUnauthorizedHandler, tokenStore } from "./api";
import { clearApiCache, unsubscribePush } from "./pwa";
import type { Me, Session } from "./types";
import { useI18n } from "../i18n";

interface Auth {
  me: Me | null;
  loading: boolean;
  signedIn: boolean;
  /** Approved or suspended: the full app (suspended sees history, no cash-out). */
  isAgent: boolean;
  startSession: (s: Session) => Promise<void>;
  refresh: () => Promise<Me | null>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setLang } = useI18n();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(Boolean(tokenStore.get()));

  const clear = useCallback(() => {
    tokenStore.set(null);
    setMe(null);
    void clearApiCache();
  }, []);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setMe(null);
      setLoading(false);
      return null;
    }
    try {
      const m = await api.get<Me>("/agent/me");
      setMe(m);
      return m;
    } catch {
      clear();
      return null;
    } finally {
      setLoading(false);
    }
  }, [clear]);

  useEffect(() => {
    setUnauthorizedHandler(clear);
    void refresh();
  }, [clear, refresh]);

  const startSession = useCallback(
    async (s: Session) => {
      await clearApiCache();
      tokenStore.set(s.accessToken);
      // No splash here: the form stays mounted until `me` lands and the routes switch.
      const m = await refresh();
      // Messages follow the agent's preferred language (spec §11), so the app does too.
      if (m?.language) setLang(m.language);
    },
    [refresh, setLang]
  );

  const signOut = useCallback(async () => {
    // While the session still exists: its push subscription goes with it.
    await unsubscribePush();
    try {
      await api.post("/agent/auth/logout");
    } catch {
      // Ending the session locally is what matters.
    }
    clear();
  }, [clear]);

  const value = useMemo<Auth>(
    () => ({
      me,
      loading,
      signedIn: Boolean(me),
      isAgent: me?.status === AgentStatus.APPROVED || me?.status === AgentStatus.SUSPENDED,
      startSession,
      refresh,
      signOut,
    }),
    [me, loading, startSession, refresh, signOut]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
